import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Menu, Phone, Plane, X } from 'lucide-react';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../../constants/contact';
import {
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  CALL_NOTE,
  COMPANY_EMAIL,
  COMPANY_LINKS,
  COMPANY_NAME,
  NAV_LINKS,
  POLICY_LINKS,
  SITE_NAME,
  SUPPORT_HOURS,
  TOP_DISCLOSURE,
} from '../../constants/site';
import '../site.css';

export function Logo({ light = false }) {
  return (
    <Link to="/" className={`fx-logo${light ? ' fx-logo--light' : ''}`} aria-label={`${SITE_NAME} home`}>
      <span className="fx-logo__mark">
        <Plane size={18} aria-hidden />
      </span>
      <span className="fx-logo__text">
        {SITE_NAME}
        <small>by {COMPANY_NAME}</small>
      </span>
    </Link>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header className="fx-header">
      <p className="fx-topbar" role="note">
        <span className="fx-wrap">{TOP_DISCLOSURE}</span>
      </p>
      <div className="fx-wrap fx-header__row">
        <Logo />
        <nav id="fx-nav" className={`fx-nav${open ? ' is-open' : ''}`} aria-label="Main">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={close}>
              {l.label}
            </Link>
          ))}
        </nav>
        <a href={`tel:${MAIN_PHONE_TEL}`} className="fx-btn fx-btn--primary fx-header__call">
          <Phone size={16} aria-hidden />
          <span>{MAIN_PHONE_DISPLAY}</span>
        </a>
        <button
          type="button"
          className="fx-header__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="fx-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="fx-footer">
      <div className="fx-wrap">
        <div className="fx-footer__grid">
          <div>
            <Logo light />
            <p className="fx-footer__about">
              An independent travel agency helping travellers plan and book flights, hotels, rental cars, cruises and
              vacation packages.
            </p>
          </div>
          <nav aria-label="Company">
            <h2>Company</h2>
            {COMPANY_LINKS.map((l) => (
              <Link key={l.to} to={l.to}>
                {l.label}
              </Link>
            ))}
          </nav>
          <nav aria-label="Policies">
            <h2>Policies</h2>
            {POLICY_LINKS.map((l) => (
              <Link key={l.to} to={l.to}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div>
            <h2>Contact</h2>
            <address className="fx-footer__contact">
              <a href={`tel:${MAIN_PHONE_TEL}`}>
                <Phone size={15} aria-hidden /> {MAIN_PHONE_DISPLAY}
              </a>
              <a href={`mailto:${COMPANY_EMAIL}`}>
                <Mail size={15} aria-hidden /> {COMPANY_EMAIL}
              </a>
              <span>
                <MapPin size={15} aria-hidden /> {ADDRESS_LINE1}, {ADDRESS_LINE2}
              </span>
              <span className="fx-footer__hours">Phone support: {SUPPORT_HOURS}</span>
            </address>
          </div>
        </div>

        <div className="fx-footer__legal">
          <p>
            <strong>Disclaimer:</strong> {SITE_NAME} ({COMPANY_NAME}) is an independent travel agency and an
            intermediary between travellers and travel providers. We are not an airline, hotel, car rental company or
            cruise line and are not affiliated with, endorsed by or acting on behalf of any of them. Every booking is
            subject to the provider&apos;s availability, fare rules and terms. A service fee may apply to bookings made
            with our help; it is always quoted and agreed before any payment. You can also book directly with any
            travel provider. {CALL_NOTE} Brand names mentioned anywhere on this website belong to their owners and are
            used only to describe travel options. See our <Link to="/disclaimer">full disclaimer</Link>.
          </p>
          <p className="fx-footer__copy">
            &copy; {new Date().getFullYear()} {COMPANY_NAME}. {SITE_NAME} is a trade name of {COMPANY_NAME}. All rights
            reserved. City and country suggestions use data from{' '}
            <a href="https://www.geonames.org/" target="_blank" rel="noopener noreferrer">
              GeoNames
            </a>{' '}
            (CC BY 4.0).
          </p>
        </div>
      </div>
    </footer>
  );
}

/** Click-to-call bar fixed to the bottom of every main site page */
function CallBar() {
  return (
    <a href={`tel:${MAIN_PHONE_TEL}`} className="fx-callbar" aria-label={`Call ${SITE_NAME} travel advisors at ${MAIN_PHONE_DISPLAY}`}>
      <span className="fx-callbar__icon">
        <Phone size={20} aria-hidden />
      </span>
      <span className="fx-callbar__text">
        <small>Call a {SITE_NAME} travel advisor · 24/7</small>
        <strong>{MAIN_PHONE_DISPLAY}</strong>
      </span>
    </a>
  );
}

export default function SiteLayout({ children }) {
  return (
    <div className="fx-root">
      <a href="#fx-main" className="fx-skip">
        Skip to content
      </a>
      <SiteHeader />
      <main id="fx-main">{children}</main>
      <SiteFooter />
      <CallBar />
    </div>
  );
}
